/**
 * Refresh-token storage utility.
 *
 * The refresh token is now an HttpOnly cookie set by the backend during login
 * and refresh. JavaScript can NOT see or modify it (that's the point — XSS-safe
 * and shared across tabs by the browser).
 *
 * The `*RefreshToken` functions below are kept as no-ops so existing callers
 * compile, but they don't do anything useful. New code should not call them.
 *
 * The user-role helpers continue to work — role lives in sessionStorage just
 * for redirect routing on the public app.
 */

import { devError } from './devLog';

const USER_ROLE_KEY = 'codezo_user_role';

// --------------------------------------------------------------------------
// Refresh token — no-ops. Cookie handled by browser + backend.
// --------------------------------------------------------------------------

/** @deprecated Refresh token is an HttpOnly cookie. This is a no-op. */
export function storeRefreshToken(_refreshToken: string): void {
	// no-op
}

/** @deprecated Refresh token is an HttpOnly cookie that JS cannot read. Always returns null. */
export function getStoredRefreshToken(): string | null {
	return null;
}

/** @deprecated Refresh token cookie is cleared by the server's logout response. */
export function clearRefreshToken(): void {
	// no-op
}

/** @deprecated We can't tell from JS — the cookie is HttpOnly. */
export function hasStoredRefreshToken(): boolean {
	return false;
}

// --------------------------------------------------------------------------
// User role — still in sessionStorage (used for routing only, not auth).
// --------------------------------------------------------------------------

export function storeUserRole(role: string): void {
	try {
		sessionStorage.setItem(USER_ROLE_KEY, role);
	} catch (error) {
		devError('Failed to store user role:', error);
	}
}

export function getStoredUserRole(): string | null {
	try {
		return sessionStorage.getItem(USER_ROLE_KEY);
	} catch (error) {
		devError('Failed to retrieve user role:', error);
		return null;
	}
}

export function clearUserRole(): void {
	try {
		sessionStorage.removeItem(USER_ROLE_KEY);
	} catch (error) {
		devError('Failed to clear user role:', error);
	}
}
